/**
 * The chat as lyrics (the Essenziale theme, or any preset via Settings): no bubbles. The assistant's
 * latest words large, the earlier ones small and fading above (`lyricsHistory`), and the user's latest
 * words under it. While the voice speaks, the words light up as they are said (`karaoke`, karaoke.ts: at
 * the reply's own pace, from how much of its audio has played). The current line is always
 * read whole: a long one gets smaller until it fits (fit-text.ts).
 * The whole conversation is one list, like a song's lyrics (lyrics-history.ts, scroll-back.ts): at rest the
 * current line is in the middle and the earlier ones fade out above it; a drag or the wheel anywhere scrolls
 * back, the line in the middle lights up, and a small button under it, a new answer or a new turn bring it
 * back to the current line.
 */
import { useEffect, useState } from 'react';
import { isThinking, type ChatEntry } from '../../core/conversation';
import { useApp } from '../../react/app';
import { useAppearance, useConversation, useTranslate } from '../../react/hooks';
import { useVoice } from '../../voice/useVoice';
import { Icon } from '../../ui-kit/Icon';
import { ThinkingDots } from '../../ui-kit/ThinkingDots';
import { useFitText } from './fit-text';
import { createPace, litWords } from './karaoke';
import { historyLines } from './lyrics-history';
import { useScrollBack } from './scroll-back';
import css from './Lyrics.module.css';

/** The `data-line` of the current line (history lines carry their entry id). */
const CURRENT = 'current';

export function LyricsChat() {
  const entries = useConversation((c) => c.entries);
  const thinking = useConversation(isThinking);
  const listening = useVoice((v) => v.activity === 'listening');
  const { lyricsHistory, touchPills, karaoke } = useAppearance();
  const t = useTranslate();

  const replies = entries.filter((e): e is Extract<ChatEntry, { role: 'assistant' }> => e.role === 'assistant');
  const current = replies.at(-1);
  const history = historyLines(entries, current?.id, lyricsHistory, touchPills);
  const lastUser = [...entries].reverse().find((e) => e.role === 'user' && (touchPills || e.source !== 'touch'));
  const lastNotice = entries.at(-1)?.role === 'notice' ? entries.at(-1) : undefined;
  const currentBox = useFitText<HTMLDivElement>(thinking ? null : current?.text);
  const { ref, browsing, focused, onScroll, surface, toEnd } = useScrollBack([current?.id, thinking, history.length]);
  // looking back, before the first scroll the current line is the one in the middle
  const lineClass = (id: string) => (browsing ? ((focused ?? CURRENT) === id ? css.focus : css.dim) : '');

  return (
    <div className={`${css.lyrics} ${browsing ? css.lookingBack : ''}`} role="log" aria-live="polite" {...surface}>
      <div ref={ref} onScroll={onScroll} tabIndex={0} aria-label={t('chat.history')} className={css.scroller}>
        {/* room above the first line and below the last: any line can be in the middle (scroll-back.ts) */}
        <div className={css.room} aria-hidden />
        {history.map(({ entry, atRest, restOpacity }) => {
          // at rest the last answers fade upwards, as they always have, and the older lines wait unseen
          const style = restOpacity !== null && !browsing ? { opacity: restOpacity } : undefined;
          const kind = entry.role === 'assistant' ? css.past : css.pastUser;
          return (
            <p key={entry.id} data-line={entry.id} style={style}
              className={`${kind} ${atRest ? '' : css.away} ${lineClass(entry.id)}`}>
              {entry.text}
            </p>
          );
        })}
        <div className={`${css.current} ${lineClass(CURRENT)}`} ref={currentBox} data-line={CURRENT}>
          {thinking ? (
            <p className={css.line}><ThinkingDots label="…" /></p>
          ) : current ? (
            <Line key={current.id} text={current.text} karaoke={karaoke} />
          ) : (
            <p className={css.empty}>{t('chat.empty')}</p>
          )}
        </div>
        {/* the user's words, as far under the current line as the line above it: at rest only */}
        <div className={css.user}>
          {listening ? (
            <span className={css.listening}><Icon name="mic" size={26} />{t('voice.listening')}</span>
          ) : lastUser && lastUser.role === 'user' ? (
            <span className={`${css.said} ${lastUser.status === 'failed' ? css.failed : ''}`}>
              {lastUser.source === 'touch' ? <Icon name="hand" size={24} /> : lastUser.source === 'voice' ? <Icon name="mic" size={24} /> : null}
              {lastUser.text}
            </span>
          ) : null}
          {lastNotice && lastNotice.role === 'notice' && (
            <span className={`${css.notice} ${css[lastNotice.tone]}`}>
              {lastNotice.label && <strong>{t(lastNotice.label)}: </strong>}
              {lastNotice.text || (lastNotice.key ? t(lastNotice.key) : '')}
            </span>
          )}
        </div>
        <div className={css.room} aria-hidden />
      </div>
      {/* its place kept at rest, so it appears without moving anything */}
      <button type="button" className={`${css.toCurrent} ${browsing ? '' : css.hidden}`} onClick={toEnd}
        tabIndex={browsing ? 0 : -1} aria-hidden={!browsing}>
        <Icon name="chevron-down" size={20} />{t('chat.back_to_current')}
      </button>
    </div>
  );
}

/** The current line; with karaoke, the words said so far are lit. */
/** The voice's pace, learned reply after reply (karaoke.ts). */
const pace = createPace();

function Line({ text, karaoke }: { text: string; karaoke: boolean }) {
  const { voice } = useApp();
  const speaking = useVoice((v) => v.replying);
  const [lit, setLit] = useState<number | null>(null);
  const words = text.split(/(\s+)/);
  const active = karaoke && speaking && voice !== null;

  useEffect(() => {
    if (!active || !voice) return;
    let raf = 0;
    const tick = () => {
      const clock = voice.avatar.clock();
      if (clock) setLit(litWords(words, clock.playedMs, pace(text, clock.totalMs)));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, voice, text]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!active || lit === null) return <p className={css.line}>{text}</p>;
  return (
    <p className={css.line}>
      {words.map((w, i) => <span key={i} className={i < lit ? css.lit : css.unlit}>{w}</span>)}
    </p>
  );
}
